import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-comments-modal',
    templateUrl: './comments-modal.component.html',
    styleUrls: ['./comments-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommentsModalComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  /** When true, Save stays enabled with an empty comment (e.g. add-edit optional pre-save comment). */
  @Input() allowEmptySubmit = false;

  /** Dialog title (add-edit uses "Add a comment"). */
  @Input() modalTitle = 'Comment';

  /** When true, show `0 / maxCommentLength` under the textarea. */
  @Input() showCharCount = false;

  /** Must match `maxlength` on the textarea when {@link showCharCount} is on. */
  @Input() maxCommentLength = 500;

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  comment: string = '';
  minimise: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.baseURLs?.api) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.cdr.detectChanges();
  }

  onCommentChange(value: string): void {
    this.comment = value;
    this.cdr.detectChanges();
  }

  /**
   * Does not call the API directly. Emits the comment string so the parent can save.
   * E-file Add/Edit: {@link AddEditComponent.onCommentsModalRequest} → `runSave` →
   * {@link AddEditService.saveFormData} → `POST .../saveJsonObject?action_code=rv8nq0`.
   */
  save(): void {
    if (!this.allowEmptySubmit && !this.comment?.trim()) {
      return;
    }
    this.requestGridUpdate.emit(this.comment ?? '');
  }

  get saveDisabled(): boolean {
    if (this.allowEmptySubmit) {
      return false;
    }
    return !this.comment?.trim();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
